/**
 * ThreadRow Component
 *
 * Single row in the thread picker showing thread preview info.
 *
 * Displays:
 * - Author avatar (colored circle with initials)
 * - Author name and timestamp
 * - Comment text preview (truncated)
 * - Reply count if > 0
 * - Chevron icon on hover
 */

import React, { useState } from 'react';
import type { Comment, TeamMember } from '../../lib/types';
import { tokens } from '../../styles/tokens';

export interface ThreadRowProps {
  /** The thread comment */
  thread: Comment;
  /** Author information */
  author: TeamMember;
  /** Number of replies */
  replyCount: number;
  /** Click handler */
  onClick: () => void;
  /** Whether this row is focused */
  isFocused: boolean;
  /** Focus handler */
  onFocus: () => void;
  /** Whether the thread is unread */
  isUnread?: boolean;
}

/**
 * Format relative time for display
 */
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffHours < 1) {
    return 'just now';
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else {
    return date.toLocaleDateString();
  }
}

/**
 * Get initials from name
 */
function getInitials(name: string): string {
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Truncate text to specified length
 */
function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + '...';
}

export const ThreadRow: React.FC<ThreadRowProps> = ({
  thread,
  author,
  replyCount,
  onClick,
  isFocused,
  onFocus,
  isUnread = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const showHighlight = isHovered || isFocused;

  return (
    <button
      type="button"
      data-thread-row
      data-testid={`thread-row-${thread.id}`}
      role="option"
      aria-selected={isFocused}
      onClick={onClick}
      onFocus={onFocus}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: 'flex',
        width: '100%',
        padding: `${tokens.spacing.s} ${tokens.spacing.m}`,
        backgroundColor: showHighlight ? tokens.colors.bgSecondary : 'transparent',
        border: 'none',
        borderBottom: `1px solid ${tokens.colors.borderDefault}`,
        cursor: 'pointer',
        textAlign: 'left',
        outline: 'none',
        transition: `background-color ${tokens.animation.duration} ${tokens.animation.timing}`,
      }}
    >
      {/* Avatar */}
      <div
        style={{
          position: 'relative',
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          backgroundColor: tokens.colors.primary,
          color: tokens.colors.textReverse,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '12px',
          fontWeight: tokens.typography.fontWeight.semibold,
          flexShrink: 0,
          marginRight: tokens.spacing.s,
        }}
      >
        {author.avatar ? (
          <img
            src={author.avatar}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              objectFit: 'cover',
            }}
          />
        ) : (
          getInitials(author.name)
        )}
        {/* Unread indicator */}
        {isUnread && (
          <span
            data-testid="unread-indicator"
            aria-label="Unread thread"
            style={{
              position: 'absolute',
              top: '-2px',
              right: '-2px',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: tokens.colors.critical,
              border: `1px solid ${tokens.colors.bgDefault}`,
            }}
          />
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Author and timestamp */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: tokens.spacing['5xs'],
          }}
        >
          <span
            style={{
              fontSize: '13px',
              fontWeight: tokens.typography.fontWeight.semibold,
              color: tokens.colors.textDefault,
            }}
          >
            {author.name}
          </span>
          <span
            style={{
              fontSize: '11px',
              color: tokens.colors.textSecondary,
            }}
          >
            {formatRelativeTime(thread.createdAt)}
          </span>
        </div>

        {/* Comment preview */}
        <p
          style={{
            margin: 0,
            fontSize: '13px',
            color: tokens.colors.textSecondary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {truncate(thread.content, 50)}
        </p>

        {/* Reply count */}
        {replyCount > 0 && (
          <span
            style={{
              fontSize: '11px',
              color: tokens.colors.textSecondary,
              marginTop: tokens.spacing['5xs'],
              display: 'block',
            }}
          >
            {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
          </span>
        )}
      </div>

      {/* Chevron on hover */}
      {showHighlight && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginLeft: tokens.spacing['2xs'],
            color: tokens.colors.primary,
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="9,18 15,12 9,6" />
          </svg>
        </div>
      )}
    </button>
  );
};
